import * as React from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import JobDetailsAddContactModalData from './JobDetailsAddContactModal';

interface ScheduleNewAddConfirmModalProps {
    jobIds: ConfirmJobIdList[];
    systemCompanyId: number;
    saveDetails(): void;
}

interface ConfirmJobIdList {
    jobId: number;
    hasInstall: boolean;    //at least one install for this job - need to show payment method for installs only (prepaid customer)
}

interface JobDetailsToConfirm {
    jobId: number;
    siteAddress: string;
    customerId: number;
    customerContactId: number;
    customerContactList: CustomerContactList[];     //list of all customer contacts, so they can change
    contactFirstName: string;
    contactLastName: string;
    contactEmail: string;
    contactMobile: string;
    contactPhone: string;
    siteContactName: string;
    siteContactPhone: string;
    gateCode: string;
    customerAccountTypeId: number;
    hasInstall: boolean;
    paymentType: number;
}

interface CustomerContactList {
    id: number;
    customerId: number;
    name: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    mobile: string;
}

const ScheduleNewAddConfirmModalData = (props: ScheduleNewAddConfirmModalProps) => {
    const [loading, setLoading] = React.useState(true);
    const [errors, setErrors] = React.useState<{ [key: string]: string }>({});
    const [detailIndex, setDetailIndex] = React.useState(0);
    const [confirmJobDetails, setConfirmJobDetails] = React.useState<JobDetailsToConfirm[]>([]);
    const [showAddContactModal, setShowAddContactModal] = React.useState(false);
    const [customerId, setCustomerId] = React.useState(-1); //used when adding a new contact
    const [saveDisabled, setSaveDisabled] = React.useState(false);
    const [scheduling, setScheduling] = React.useState(false);

    React.useEffect(() => {
        if (props.jobIds.length > 0) {
            getConfirmJobDetails();
        }
    }, [props.jobIds]);

    const getConfirmJobDetails = async () => {
        const token = await authService.getAccessToken();

        var jobsToConfirm = {
            jobIds: props.jobIds
        };

        axios.post('api/Schedule/GetJobsToConfirm', jobsToConfirm, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            setConfirmJobDetails(res.data);
            setSaveDisabled(false);
            setLoading(false);
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    const handleCustomerContactChange = (e: any) => {
        e.preventDefault();

        let itemDetails = [...confirmJobDetails];
        let itemDetail = itemDetails[detailIndex];
        if (itemDetail) {
            itemDetail.customerContactId = e.target.value * 1;
        }
        setConfirmJobDetails(itemDetails);
    }

    const addContactModal = (e: any) => {
        e.preventDefault();
        //show popup to add contact
        let itemDetails = [...confirmJobDetails];
        let itemDetail = itemDetails[detailIndex];
        if (itemDetail) {
            setShowAddContactModal(true);
            setCustomerId(itemDetail.customerId);
        }
    }

    const hideAddContact = () => {
        setShowAddContactModal(false);
        setCustomerId(-1);
    }

    const saveAddContact = (contactDetails: CustomerContactList) => {
        let itemDetails = [...confirmJobDetails];

        //add the contact to all contact lists for this customer
        var custItemDetails = itemDetails.filter(i => i.customerId === contactDetails.customerId);
        for (var i = 0; i < custItemDetails.length; i++) {
            var custItemDetail = custItemDetails[i];
            var custContacts = [...custItemDetail.customerContactList];
            custContacts.push(contactDetails);
            custItemDetail.customerContactList = custContacts;
        }

        //update the current item to use the customer contact we just added
        let itemDetail = itemDetails[detailIndex];
        if (itemDetail) {
            itemDetail.customerContactId = contactDetails.id;
        }

        setShowAddContactModal(false);
        setCustomerId(-1);
        setConfirmJobDetails(itemDetails);
    }

    const handleSiteContactNameChange = (e: any) => {
        e.preventDefault();

        let itemDetails = [...confirmJobDetails];
        let itemDetail = itemDetails[detailIndex];
        if (itemDetail) {
            itemDetail.siteContactName = e.target.value;
        }
        setConfirmJobDetails(itemDetails);
    }

    const handleSiteContactPhoneChange = (e: any) => {
        e.preventDefault();

        let itemDetails = [...confirmJobDetails];
        let itemDetail = itemDetails[detailIndex];
        if (itemDetail) {
            itemDetail.siteContactPhone = e.target.value;
        }
        setConfirmJobDetails(itemDetails);
    }

    const handlePaymentTypeChange = (e: any) => {
        e.preventDefault();

        let itemDetails = [...confirmJobDetails];
        let itemDetail = itemDetails[detailIndex];
        if (itemDetail) {
            itemDetail.paymentType = e.target.value;
        }
        setConfirmJobDetails(itemDetails);
    }

    const handleGateCodeChange = (e: any) => {
        e.preventDefault();

        let itemDetails = [...confirmJobDetails];
        let itemDetail = itemDetails[detailIndex];
        if (itemDetail) {
            itemDetail.gateCode = e.target.value;
        }
        setConfirmJobDetails(itemDetails);
    }
   
    const validate = () => {
        let errorList: { [key: string]: string } = {};
        var formIsValid = true;
        let detail = confirmJobDetails[detailIndex];

        if (detail.customerContactId < 1) {
            formIsValid = false;
            errorList["customerContactId"] = "Customer Contact is required";
        }

        if (detail.customerAccountTypeId === 1 && detail.hasInstall) {
            //payment method is required
            if (detail.paymentType < 1) {
                formIsValid = false;
                errorList["paymentType"] = "Payment Method is required";
            }
        }

        if (props.systemCompanyId === 3) {
            if (!detail.siteContactName || detail.siteContactName === "") {
                formIsValid = false;
                errorList["siteContactName"] = "Site Contact Name is required";
            }

            if (!detail.siteContactPhone || detail.siteContactPhone === "") {
                formIsValid = false;
                errorList["siteContactPhone"] = "Site Contact Phone is required";
            }
        }

        setErrors(errorList);
        return formIsValid;
    }

    const saveModal = () => {
        if (validate()) {
            if (detailIndex === (confirmJobDetails.length - 1)) {
                if (saveDisabled) {
                    return;
                }
                setSaveDisabled(true);
                setScheduling(true);
                //updated all the jobs selected so save
                saveJobContacts();
            } else {
                setDetailIndex(detailIndex + 1);
            }
        } else {
            toast.error("Please fix the errors on screen!");
        }
    }

    const saveJobContacts = async () => {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        var data = {
            details: confirmJobDetails,
            subId: user.sub
        };

        axios.post('api/Schedule/SaveConfirmJobContacts', data, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data.isError) {
                var errors = res.data.messages as any[];
                errors.map(function (error: any) {
                    toast.error(error.content);
                });
                setScheduling(false);
            } else {
                setScheduling(false);
                //Dunny don't need a toast as Dash still needs to email the customer and will display a separate toast
                if (props.systemCompanyId === 2) {
                    toast.success("Job confirmed successfully");
                }
                props.saveDetails();
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    let addEditContact = <JobDetailsAddContactModalData showModal={showAddContactModal} hideModal={hideAddContact} customerId={customerId} id={-1} saveModal={saveAddContact} />

    const renderDetails = (
        <div>
            <div className="static-modal">
                {addEditContact}
            </div>
            <div>
                <h1>Confirm Job Details</h1>
                <label className="input-group" htmlFor="phone">
                    <span className="label">Site Address</span>
                    {confirmJobDetails[detailIndex] ? confirmJobDetails[detailIndex].siteAddress : ""}
                </label>

                <label className="input-group" htmlFor="contact">
                    <span className="label">Contact</span>
                    <select className="select marginRight10" id="contact" name="customerContactId" value={confirmJobDetails[detailIndex] ? confirmJobDetails[detailIndex].customerContactId : 0} onChange={(e) => handleCustomerContactChange(e)}>
                        <option hidden defaultValue="-1"></option>
                        {confirmJobDetails[detailIndex] ? confirmJobDetails[detailIndex].customerContactList.map(custcon =>
                            <option key={custcon.id} value={custcon.id}>{custcon.name}</option>
                        ) : ""};
                    </select>
                    <a href="#" onClick={(e) => addContactModal(e)}>Add New</a>
                </label>
                <span className={errors["customerContactId"] ? "label errors marginLeftLabel" : "hidden"}>{errors["customerContactId"]}</span>

                <div>
                    <label className="input-group" htmlFor="siteContactName">
                        <span className="label">Site Contact Name</span>
                        <input className="input" type="text" maxLength={50} id="siteContactName" name="siteContactName" value={confirmJobDetails[detailIndex] ? confirmJobDetails[detailIndex].siteContactName : ""} onChange={(e) => handleSiteContactNameChange(e)}></input>
                    </label>
                </div>
                <span className={errors["siteContactName"] ? "label errors marginLeftLabel" : "hidden"}>{errors["siteContactName"]}</span>

                <div>
                    <label className="input-group" htmlFor="siteContactPhone">
                        <span className="label">Site Contact Phone</span>
                        <input className="input" type="text" maxLength={50} id="siteContactPhone" name="siteContactPhone" value={confirmJobDetails[detailIndex] ? confirmJobDetails[detailIndex].siteContactPhone : ""} onChange={(e) => handleSiteContactPhoneChange(e)}></input>
                    </label>
                </div>
                <span className={errors["siteContactPhone"] ? "label errors marginLeftLabel" : "hidden"}>{errors["siteContactPhone"]}</span>

                <div className={confirmJobDetails[detailIndex] && confirmJobDetails[detailIndex].customerAccountTypeId === 1 && confirmJobDetails[detailIndex].hasInstall ? "" : "hidden"}>
                    <label className="input-group" htmlFor="paymentType">
                        <span className="label">Payment Method</span>
                        <select className="select" id="paymentType" name="paymentType" value={confirmJobDetails[detailIndex] ? confirmJobDetails[detailIndex].paymentType : -1 } onChange={(e) => handlePaymentTypeChange(e)}>
                            <option defaultValue="-1" value={-1}></option>
                            <option value={3}>Credit Card</option>
                            <option value={4}>Direct Debit</option>
                        </select>
                    </label>
                </div>
                <span className={errors["paymentType"] ? "label errors marginLeftLabel" : "hidden"}>{errors["paymentType"]}</span>

                <div>
                    <label className="input-group" htmlFor="gatecode">
                        <span className="label">Gate Code</span>
                        <input className="input" type="text" maxLength={20} id="gatecode" name="gateCode" value={confirmJobDetails[detailIndex] ? confirmJobDetails[detailIndex].gateCode : ""} onChange={(e) => handleGateCodeChange(e)}></input>
                    </label>
                </div>

                <button className="popup-button--yes marginLeftLabel" type="button" onClick={saveModal} disabled={saveDisabled || scheduling} >{detailIndex == (confirmJobDetails.length - 1) ? "Confirm" : "Next"}</button>
            </div>
        </div>
    )

    let contents = loading
        ? <p><em>Loading...</em></p>
        : renderDetails;

    return (
        <div>
            {contents}
        </div>
    )

}

export default ScheduleNewAddConfirmModalData;